import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Phone, Mail, MapPin, MessageCircle } from 'lucide-react';
import SEO from '../components/SEO';
import { companySchema, getPageMetadata, SITE } from '../config/seo';

const ContactPage = () => {
  const { language } = useLanguage();

  const getLocalizedText = (texts) => texts[language] || texts.tc;
  const meta = getPageMetadata('contact', language);
  const schemas = [
    companySchema(),
    { '@type': 'ContactPage', name: meta.title, url: `${SITE.url}/contact`, mainEntity: { '@id': `${SITE.url}/#organization` } },
  ];

  const contactInfo = [
    {
      icon: <Phone className="w-6 h-6" />,
      title: { tc: '電話', cn: '电话', en: 'Phone' },
      content: '2543-9248 / 2543-9249',
      link: 'tel:25439248'
    },
    {
      icon: <MessageCircle className="w-6 h-6" />,
      title: { tc: 'WhatsApp', cn: 'WhatsApp', en: 'WhatsApp' },
      content: language === 'tc' ? '點擊聯絡我們' : language === 'cn' ? '点击联络我们' : 'Click to Contact Us',
      link: 'https://wa.me/85298605400'
    },
    {
      icon: <Mail className="w-6 h-6" />,
      title: { tc: '電郵', cn: '电邮', en: 'Email' },
      content: 'info@shukee.com.hk',
      link: 'mailto:info@shukee.com.hk'
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: { tc: '地址', cn: '地址', en: 'Address' },
      content: language === 'tc' 
        ? '西營盤西區副食品批發市場A41號'
        : language === 'cn'
        ? '西营盘西区副食品批发市场A41号'
        : 'Stall A41, Western Wholesale Food Market, Sai Ying Pun, Hong Kong',
      link: 'https://maps.google.com/?q=西營盤西區副食品批發市場A41號'
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO {...meta} path="/contact" language={language} schemas={schemas} />
      <Header />

      <section className="py-20 bg-gradient-to-br from-cyan-50 to-white" data-testid="contact-section">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <h1 className="text-5xl font-bold mb-6 gradient-text" data-testid="contact-title">
              {language === 'tc' ? '聯絡我們' : language === 'cn' ? '联络我们' : 'Contact Us'}
            </h1>
            <p className="text-xl text-slate-600">
              {language === 'tc' 
                ? '作為現代廚師和餐飲業界最可靠的合作夥伴，我們期待您的查詢。'
                : language === 'cn'
                ? '作为现代厨师和餐饮业界最可靠的合作伙伴，我们期待您的查询。'
                : 'As the most reliable partner of modern chefs and the restaurant industry, we look forward to your inquiries.'}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {contactInfo.map((info, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="glass p-8 rounded-3xl hover:shadow-xl transition-all"
                data-testid={`contact-card-${index}`}
              >
                <div className="w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-2xl flex items-center justify-center text-white mb-4">
                  {info.icon}
                </div>
                <h3 className="text-xl font-bold mb-2">{getLocalizedText(info.title)}</h3>
                {info.link ? (
                  <a 
                    href={info.link} 
                    target={info.link.startsWith('http') ? '_blank' : '_self'}
                    rel={info.link.startsWith('http') ? 'noopener noreferrer' : ''}
                    className="text-primary text-lg hover:underline block"
                  >
                    {info.content}
                  </a>
                ) : (
                  <p className="text-slate-600 text-lg">{info.content}</p>
                )}
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16 glass p-8 rounded-3xl overflow-hidden"
          >
            <h3 className="text-2xl font-bold mb-6 text-center">
              {language === 'tc' ? '我們的位置' : language === 'cn' ? '我们的位置' : 'Our Location'}
            </h3>
            <div className="aspect-video rounded-2xl overflow-hidden">
              <iframe
                src="https://maps.google.com/maps?width=100%25&amp;height=600&amp;hl=zh-TW&amp;q=西營盤西區副食品批發市場A41號+(樹記海鮮)&amp;t=&amp;z=17&amp;ie=UTF8&amp;iwloc=B&amp;output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="西營盤西區副食品批發市場A41號"
              ></iframe>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;